import api from './api.service';

export type UaeExchangeDocumentType =
  | 'SalesInvoice'
  | 'DirectPurchaseInvoice'
  | 'SupplierInvoice'
  | 'CustomerCreditNote';

export interface UaeReadinessGroup {
  id: string;
  label: string;
  ready: boolean;
  missing: string[];
}

export interface UaeReadinessResult {
  ready: boolean;
  groups: UaeReadinessGroup[];
}

export interface UaeExchangeResult {
  transmissionId: string;
  exchangeUuid: string;
  invoiceHash: string;
  qrPayload: string;
  qrImageDataUrl: string;
  status: string;
  clearanceNumber?: string;
  rejectionReason?: string;
  providerEntryId?: string;
  providerStatus?: string;
  providerTaxStatus?: string;
  idempotentReplay?: boolean;
}

export interface InvoiceExchangeTransmission {
  id: string;
  company_id: string;
  document_type: string;
  document_id: string;
  jurisdiction: string;
  adapter_code: string;
  exchange_uuid?: string | null;
  provider_entry_id?: string | null;
  provider_status?: string | null;
  provider_tax_status?: string | null;
  direction?: string | null;
  invoice_hash?: string | null;
  qr_payload?: string | null;
  qr_image_data_url?: string | null;
  status: string;
  clearance_number?: string | null;
  rejection_reason?: string | null;
  request_at: string;
  response_at?: string | null;
}

export interface AspConnectionDto {
  id: string;
  company_id: string;
  provider_code: string;
  enabled: boolean;
  environment: 'SANDBOX' | 'LIVE';
  api_base_url: string | null;
  external_company_id: string | null;
  peppol_participant_id: string | null;
  webhook_url: string | null;
  webhook_status: string | null;
  webhook_subscription_id: string | null;
  has_api_key: boolean;
  has_webhook_secret: boolean;
  notes: string | null;
}

export interface InboundEInvoiceDto {
  id: string;
  provider_entry_id: string;
  document_id?: string | null;
  seller_vat_trn?: string | null;
  seller_name?: string | null;
  payable_amount?: number | null;
  currency_code?: string | null;
  issue_date?: string | null;
  match_status: string;
  matched_document_type?: string | null;
  matched_document_id?: string | null;
  invoice_type_code?: string | null;
  created_at?: string;
}

const complianceService = {
  async generateUaeEInvoice(
    invoiceId: string,
    documentType: UaeExchangeDocumentType = 'SalesInvoice'
  ): Promise<UaeExchangeResult> {
    const res = await api.post(`/compliance/uae/generate/${invoiceId}`, {}, {
      params: { document_type: documentType },
    });
    return res.data?.data ?? res.data;
  },

  async getUaeTransmissionStatus(uuid: string): Promise<Record<string, unknown>> {
    const res = await api.get(`/compliance/uae/status/${uuid}`);
    return res.data?.data ?? res.data;
  },

  async listTransmissions(params?: {
    document_type?: string;
    document_id?: string;
  }): Promise<InvoiceExchangeTransmission[]> {
    const res = await api.get('/compliance/uae/transmissions', { params });
    return res.data?.data ?? res.data ?? [];
  },

  async validateTrn(trn: string): Promise<{ trn: string; is_valid: boolean }> {
    const res = await api.post('/compliance/uae/validate-trn', { trn });
    return res.data?.data ?? res.data;
  },

  async getSalesInvoiceReadiness(invoiceId: string): Promise<UaeReadinessResult> {
    const res = await api.get(`/compliance/uae/readiness/${invoiceId}`);
    return res.data?.data ?? res.data;
  },

  async listAspConnections(): Promise<AspConnectionDto[]> {
    const res = await api.get('/compliance/asp/connections');
    return res.data?.data ?? res.data ?? [];
  },

  async upsertAspConnection(body: Record<string, unknown>): Promise<AspConnectionDto> {
    const res = await api.put('/compliance/asp/connections', body);
    return res.data?.data ?? res.data;
  },

  async testAspConnection(id: string): Promise<{ ok: boolean }> {
    const res = await api.post(`/compliance/asp/connections/${id}/test`);
    return res.data?.data ?? res.data;
  },

  async bootstrapAspWebhook(
    id: string,
    body?: { webhookUrl?: string }
  ): Promise<{ subscriptionId: string; hasSecret: boolean; status: string }> {
    const res = await api.post(`/compliance/asp/connections/${id}/webhooks/bootstrap`, body || {});
    return res.data?.data ?? res.data;
  },

  async listInbound(params?: { match_status?: string }): Promise<InboundEInvoiceDto[]> {
    const res = await api.get('/compliance/asp/inbound', { params });
    return res.data?.data ?? res.data ?? [];
  },

  async matchInbound(
    id: string,
    body: { document_type: string; document_id: string }
  ): Promise<InboundEInvoiceDto> {
    const res = await api.post(`/compliance/asp/inbound/${id}/match`, body);
    return res.data?.data ?? res.data;
  },

  async ignoreInbound(id: string): Promise<InboundEInvoiceDto> {
    const res = await api.post(`/compliance/asp/inbound/${id}/ignore`);
    return res.data?.data ?? res.data;
  },

  async refreshAspEntryStatus(entryId: string): Promise<Record<string, unknown>> {
    const res = await api.get(`/compliance/asp/entries/${entryId}/status`);
    return res.data?.data ?? res.data;
  },
};

export default complianceService;
